<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="yellow darken-2"
    />
    <v-skeleton-loader
      v-if="loading"
      type="table-heading, list-item-two-line, list-item-two-line"
    />
    <v-sheet
      class="pr-2"
      elevation="1"
      rounded
      :outlined="grouped"
      :class="statusColorClass"
    >
      <v-card
        v-if="!loading"
        :id="cardIdentifier"
        ref="cardBody"
        elevation="0"
        tile
        :class="{'pe-card-shared' : shared, 'pe-card-survey' : surveyValidation}"
        class="section-card-body"
        style="position: relative;"
      >
        <v-overlay
          absolute
          :value="overlay"
          class="text-center"
        >
          <v-progress-circular
            indeterminate
            :size="50"
            color="primary"
          />
          <p class="text-overline white--text mt-4">
            committing changes...
          </p>
        </v-overlay>
        <v-overlay
          absolute
          :value="cardUpdatedByOther"
          class="text-center"
        >
          <p class="text-overline white--text mt-4">
            This card has been updated by another user. Please refresh to see the latest changes.
          </p>
          <v-btn
            color="primary"
            @click="onCardRefresh"
          >
            Refresh
          </v-btn>
        </v-overlay>
        <v-card-text>
          <v-row align="center">
            <v-hover v-slot="{ hover }">
              <v-col
                class="flex-grow text-caption ml-n3"
              >
                <v-icon
                  v-if="hover && !create && hasShare"
                  small
                  color="primary"
                  @click="onCardShare"
                >
                  mdi-link-variant
                </v-icon>
                <span
                  v-if="!grouped"
                  class="my-4 text-subtitle-1 text-uppercase font-weight-bold"
                  :class="{'ml-5' : !(hover && !create && hasShare)}"
                >
                  {{ title }} <small v-if="subtitle && collapsed">{{ subtitle }}</small>
                </span>
              </v-col>
            </v-hover>
            <v-col
              v-if="showHeader"
              cols="auto"
            >
              <slot
                v-if="!create"
                name="header"
              />
              <slot name="extras" />
            </v-col>
            <v-col
              cols="auto"
            >
              <v-btn
                v-if="hasDelete && !viewOnly"
                icon
                @click="deleteDialog = true"
              >
                <v-icon>delete</v-icon>
              </v-btn>
              <v-btn
                v-if="onRefresh !== null && !create"
                icon
                @click="onCardRefresh"
              >
                <v-icon>
                  mdi-refresh
                </v-icon>
              </v-btn>
              <v-btn
                icon
                @click="collapsed = !collapsed"
              >
                <v-icon>
                  {{ collapsableIcon }}
                </v-icon>
              </v-btn>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-if="!viewOnly"
                    :color="actionButtonIconColor"
                    type="submit"
                    small
                    icon
                    v-bind="attrs"
                    :disabled="!locked && invalid"
                    v-on="on"
                    @click="onCardSubmit"
                  >
                    <v-icon>{{ actionButtonIcon }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ actionButtonText }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
        <v-expand-transition>
          <v-card-text v-show="!collapsed">
            <slot />

            <v-row v-if="surveyValidation">
              <v-spacer />
              <v-col cols="auto">
                <v-btn
                  v-if="!surveyAccepted"
                  :disabled="surveyRejected"
                  class="mx-2"
                  color="error"
                  @click="surveyRejectDialog = true"
                >
                  Decline
                </v-btn>
                <v-btn
                  v-if="!surveyRejected"
                  :disabled="surveyAccepted"
                  class="mx-2"
                  :color="surveyTextColor"
                  @click="onSurveySubmit"
                >
                  {{ surveyText }}
                </v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-spacer />
              <v-col
                v-if="hasFooter"
                cols="auto"
              >
                <small class="grey--text text-caption">{{ footer }}</small>
              </v-col>
            </v-row>
          </v-card-text>
        </v-expand-transition>
        <v-card-text
          v-if="hasDocuments && cardDocuments.length"
          class="py-0"
        >
          <AttachmentList
            enable-delete
            :files="cardDocuments"
            @delete-file="doc => onSelectFileToDelete(doc)"
          />
        </v-card-text>
        <v-card-actions v-if="hasDocuments">
          <v-spacer />
          <v-btn
            v-if="!create && !viewOnly"
            small
            color="primary"
            @click="documentAddDialog = true"
          >
            add document
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-sheet>
    <PEPartyCardDeleteDialog
      v-model="deleteDialog"
      :title="title"
      :on-card-delete="onCardDelete"
    />
    <PEPartyAddDocumentDialog
      v-if="hasDocuments"
      v-model="documentAddDialog"
      :document-types="documentTypes"
      :identifier="documentIdentifier"
      :title="title"
    />
    <PEPartyDeleteDocumentDialog
      v-model="documentDeleteDialog"
      :document="selectedDocument"
      :on-document-delete="onDeleteFile"
    />
    <PEPartySurveyRejectDialog
      v-model="surveyRejectDialog"
      :title="title"
      :on-card-reject="onSurveyReject"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import AttachmentList from '@/components/DocketEntry/AttachmentList.vue'
import PEPartyAddDocumentDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyAddDocumentDialog.vue'
import PEPartyCardDeleteDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyCardDeleteDialog.vue'
import PEPartyDeleteDocumentDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyDeleteDocumentDialog.vue'
import PEPartySurveyRejectDialog from '../SectionTools/PEPartySurveyRejectDialog.vue'

const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor

export default {
  name: 'GenericCard',

  components: {
    AttachmentList,
    PEPartyAddDocumentDialog,
    PEPartyCardDeleteDialog,
    PEPartyDeleteDocumentDialog,
    PEPartySurveyRejectDialog,
  },

  props: {
    cardIdentifier: {
      type: String,
      default: null,
    },

    create: {
      type: Boolean,
      default: false,
    },

    dirty: {
      type: Boolean,
      default: false,
    },

    documentIdentifier: {
      type: String,
      default: '',
    },

    documentTypes: {
      type: Array,
      default: _ => ([]),
    },

    grouped: {
      type: Boolean,
      default: false,
    },

    hasDelete: {
      type: Boolean,
      default: false,
    },

    hasDocuments: {
      type: Boolean,
      default: false,
    },

    hasEdit: {
      type: Boolean,
      default: true,
    },

    hasFooter: {
      type: Boolean,
      default: false,
    },

    hasShare: {
      type: Boolean,
      default: false,
    },

    headerOnlyCollapsed: {
      type: Boolean,
      default: false,
    },

    invalid: {
      type: Boolean,
      default: false,
    },

    locked: {
      type: Boolean,
      default: false,
    },

    onAction: {
      type: AsyncFunction,
      required: true,
    },

    onDelete: {
      type: AsyncFunction,
      default: _ => {},
    },

    onRefresh: {
      type: AsyncFunction,
      default: null,
    },

    section: {
      type: Object,
      default: _ => ({}),
    },

    subtitle: {
      type: String,
      default: null,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      collapseSubscription: null,
      collapsed: false,
      deleteDialog: false,
      documentAddDialog: false,
      documentDeleteDialog: false,
      overlay: false,
      selectedDocument: {},
      shareHash: null,
      surveyRejectDialog: false,
    }
  },

  computed: {
    actionButtonIcon () {
      return this.locked ? 'mdi-pencil' : 'mdi-content-save'
    },

    actionButtonIconColor () {
      return this.locked ? 'primary' : 'success'
    },

    actionButtonText () {
      return this.locked ? 'Edit' : 'Save'
    },

    cardDocuments () {
      return this.hasDocuments
        ? this.pePartyDocumentsByIdentifier(this.documentIdentifier, this.peParty.id).
          map(doc => ({ ...doc, title: this.documentTypes.find(t => t.value === doc.document_type)?.text }))
        : []
    },

    cardUpdatedByOther: {
      get () {
        return this.pePartyCardsOtherUpdated[this.cardIdentifier] === true
      },

      set (value) {
        this.pePartySetOthersUpdated({ identifier: this.cardIdentifier, value })
      },
    },

    collapsableIcon () {
      return this.collapsed ? 'unfold_more' : 'unfold_less'
    },

    footer () {
      return `ID: ${this.section?.id}, Last updated on ${moment(this.section?.updated_at).format('LLL')}`
    },

    loading () {
      if (this.create) return false
      return this.genericSectionLoading
    },

    shared () {
      return this.shareHash === `#${this.cardIdentifier}`
    },

    showHeader () {
      if (!this.$slots.header) return false
      return this.headerOnlyCollapsed ? this.collapsed : true
    },

    statusColorClass () {
      if (this.locked) return 'light-green'
      if (this.invalid) return 'red'
      if (this.dirty) return 'amber'

      return 'amber'
    },

    statusLabel () {
      if (this.locked) return 'locked'
      if (this.invalid) return 'invalid'
      if (this.dirty) return 'editing'

      return 'editing'
    },

    surveyAccepted () {
      return this.peSurveyCardAcceptedCard(this.surveyIdentifier)
    },

    surveyRejected () {
      return this.peSurveyCardRejectedCard(this.surveyIdentifier)
    },

    surveyText () {
      if (this.surveyAccepted) return 'Accepted'
      return this.locked ? 'Review' : 'Save'
    },

    surveyTextColor () {
      return this.locked ? 'warning' : 'success'
    },

    viewOnly () {
      if (this.create) return false
      if (!this.hasEdit) return true
      if (this.surveyValidation) return true
      return !(this.peUserCanUpdate === true)
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'genericSectionLoading',
      'peParty',
      'pePartyCardsOtherUpdated',
      'pePartyDocumentsByIdentifier',
      'pePendingCardScrollIdentifier',
      'peSharedQESliderCurrentSectionRoute',
      'peSharedQESliderPartyId',
      'peSharedQESliderStatus',
      'peSurveyCardAcceptedCard',
      'peSurveyCardData',
      'peSurveyCardRejectedCard',
      'peUserCanUpdate',
    ]),
  },

  created () {
    this.collapseSubscription = this.$store.subscribeAction(action => {
      if (action.type === `${PARTY_NAMESPACE}/peSetCollapsedAllCards`) {
        this.collapsed = action.payload
      } else if (action.type === `${PARTY_NAMESPACE}/peSetCollapsedCardsByIdentifier`) {
        if (this.cardIdentifier.includes(action.payload.identifier)) {
          this.collapsed = true
        }
      }
    })

    this.shareHash = this.$router.currentRoute.hash
  },

  mounted () {
    if (this.shared) {
      setTimeout(_ => { this.$refs.cardBody?.$el.scrollIntoView({ behavior: 'smooth' }) }, 500)
      return
    }

    // Picks up cards that get torn down and remounted with a new key when a create commits their
    // real id (e.g. `education-history-card-new` -> `education-history-card-42`), since the instance
    // that requested the scroll no longer exists by the time this one mounts.
    this.scrollToPendingCard()
  },

  beforeDestroy () {
    if (this.collapseSubscription) this.collapseSubscription()
  },

  methods: {
    buildChangesPayload () {
      // Build the changes payload based on the survey identifier
      const surveyData = this.peSurveyCardData(this.surveyIdentifier)
      if (!surveyData) return []

      // Extract the model type and record ID from the identifier
      // e.g., "email-card-123" -> model_type: "email", record_id: "123"
      // e.g., "physical-address-card-123" -> model_type: "address", record_id: "123"
      const parts = this.surveyIdentifier.split('-')
      if (parts.length < 3) return []

      const recordId = parts[parts.length - 1] // Always take the last part as the ID

      // Determine model type based on the identifier pattern
      let modelType
      if (this.surveyIdentifier.startsWith('physical-address-card-') || this.surveyIdentifier.startsWith('mailing-address-card-')) {
        modelType = 'address'
      } else if (this.surveyIdentifier.startsWith('email-card-')) {
        modelType = 'email'
      } else if (this.surveyIdentifier.startsWith('phone-card-')) {
        modelType = 'phone'
      } else {
        // Fallback: use first part
        modelType = parts[0]
      }

      // Get the fields that were changed based on the survey data
      const fields = this.getChangedFields(surveyData, modelType)

      return [{
        fields: fields,
        inputs: surveyData.inputs || this.section,
        model_type: modelType,
        record_id: recordId,
      }]
    },

    callKioskApproveAPI () {
      // Only call if we have the necessary data for Kiosk integration
      const surveyData = this.peSurveyCardData(this.surveyIdentifier)
      if (surveyData && this.isKioskSurvey()) {
        const changes = this.buildChangesPayload()
        return this.peSurveyApproveKioskChanges({
          changes: changes,
          surveyId: this.getSurveyId(),
        })
      }
      return Promise.resolve()
    },

    callKioskRejectAPI () {
      // Only call if we have the necessary data for Kiosk integration
      const surveyData = this.peSurveyCardData(this.surveyIdentifier)
      if (surveyData && this.isKioskSurvey()) {
        const changes = this.buildChangesPayload()
        return this.peSurveyRejectKioskChanges({
          changes: changes,
          surveyId: this.getSurveyId(),
        }).then(_ => {
          this.displayAlert('success', 'Changes rejected and sent to Kiosk')
        }).catch(error => {
          this.displayAlert('error', `Failed to reject changes in Kiosk: ${parseErrorResponseMessage(error)}`)
        })
      }
      return Promise.resolve()
    },

    copyToClipboard (text, description) {
      description ||= 'Value'
      navigator.clipboard.writeText(text).
        then(_ => {
          this.displayAlert('success', `${description} copied to the clipboard`)
        }).
        catch(_ => {
          this.displayAlert('error', `There was an error copying the ${description} to the clipboard`)
        })
    },

    getChangedFields (surveyData, modelType) {
      // Extract field names based on the model type and survey data
      // This is a simplified version - you might need to adjust based on actual survey data structure
      switch (modelType) {
        case 'email':
          return ['address', 'owner', 'primary'].filter(field => surveyData.inputs && surveyData.inputs[field] !== undefined)
        case 'phone':
          return ['owner', 'country_prefix', 'phone_number', 'phone_type'].filter(field => surveyData.inputs && surveyData.inputs[field] !== undefined)
        case 'address':
          return ['address', 'city', 'county', 'state', 'zip', 'country_id', 'same_as_physical'].filter(field => surveyData.inputs && surveyData.inputs[field] !== undefined)
        default:
          return []
      }
    },

    getSurveyId () {
      // Get the survey ID from the party's surveys
      const surveys = this.peParty.surveys || []
      // For now, assume we're working with the first survey that contains Kiosk data
      // This might need to be more sophisticated based on the actual data structure
      const kioskSurvey = surveys.find(survey =>
        survey.cards && Object.keys(survey.cards).some(key =>
          key.startsWith('email-card-') || key.startsWith('phone-card-') || key.startsWith('physical-address-card-') ||
            key.startsWith('mailing-address-card-')
        )
      )
      return kioskSurvey ? kioskSurvey.id : null
    },

    isKioskSurvey () {
      // Check if this survey is from Kiosk by looking at survey identifier patterns
      // Kiosk surveys typically have identifiers like "email-card-123" or "phone-card-456"
      return this.surveyIdentifier && (
        this.surveyIdentifier.startsWith('email-card-') ||
        this.surveyIdentifier.startsWith('phone-card-') ||
        this.surveyIdentifier.startsWith('physical-address-card-') ||
        this.surveyIdentifier.startsWith('mailing-address-card-')
      )
    },

    onCardDelete () {
      this.overlay = true
      this.onDelete().
        then(_ => {
          this.displayAlert('success', `${this.title} Deleted Successfully`)
        }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error))).
        finally(_ => {
          this.deleteDialog = false
          this.overlay = false
        })
    },

    onCardRefresh () {
      this.overlay = true
      this.onRefresh().
        then(_ => {
          this.displayAlert('success', `${this.title} Refreshed Successfully`)
        }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error))).
        finally(_ => {
          this.overlay = false
          this.cardUpdatedByOther = false
        })
    },

    onCardShare () {
      let routeName = null
      let partyId = null
      if (this.peSharedQESliderStatus) {
        routeName = this.peSharedQESliderCurrentSectionRoute
        partyId = this.peSharedQESliderPartyId
      } else {
        routeName = this.$router.currentRoute.name
        partyId = this.$router.currentRoute.params.id
      }

      const { href } = this.$router.resolve({ name: routeName, params: { id: partyId } })
      const fullUrl = `${window.location.origin}${href}#${this.cardIdentifier}`
      this.copyToClipboard(fullUrl, 'card share link')
    },

    onCardSubmit () {
      if (this.locked) {
        this.onAction()
        this.collapsed = false
      } else {
        const isCreated = this.create
        this.overlay = true
        this.
          onAction().
          then(next => {
            if (next === false) return

            this.displayAlert('success', `${this.title} ${isCreated ? 'Created' : 'Updated'} Successfully`)
            if (this.surveyValidation) this.peSurveyCardAcceptCard({ identifier: this.surveyIdentifier })

            this.peSetPendingCardScrollIdentifier(this.cardIdentifier)
            this.scrollToPendingCard()

            if (isCreated) this.$emit('created', next)
            else this.$emit('updated', next)
          }).
          catch(error => this.displayAlert('error', parseErrorResponseMessage(error))).
          finally(_ => {
            this.overlay = false
          })
      }
    },

    onDeleteFile () {
      const { blob } = this.selectedDocument.document

      this.
        peDeletePartyDocument(this.selectedDocument).
        then(_ => {
          this.displayAlert('success', `${this.selectedDocument.title} [${blob.filename}] Deleted Successfully`)
          this.documentDeleteDialog = false
        }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error)))
    },

    onSelectFileToDelete (doc) {
      this.selectedDocument = this.cardDocuments.find(d => d.id === doc.documentId)
      this.documentDeleteDialog = true
    },

    onSurveyReject () {
      this.peSurveyCardRejectCard({ identifier: this.surveyIdentifier }).
        then(_ => {
          this.surveyRejectDialog = false
          // If this is a Kiosk survey, also call the backend API
          this.callKioskRejectAPI()
        }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error)))
    },

    onSurveySubmit () {
      // For new Kiosk records (create=true), skip the normal submit and only call approve API
      // The approve API will create the record in LLP and notify Kiosk
      if (this.create && this.isKioskSurvey() && !this.locked) {
        this.overlay = true
        this.callKioskApproveAPI().
          then(_ => {
            this.displayAlert('success', `${this.title} Created and Approved Successfully`)
            if (this.surveyValidation) this.peSurveyCardAcceptCard({ identifier: this.surveyIdentifier })
          }).
          catch(error => this.displayAlert('error', parseErrorResponseMessage(error))).
          finally(_ => {
            this.overlay = false
          })
      } else {
        // Normal flow: call the original submit logic first
        this.onCardSubmit()

        // Only call Kiosk approve API if we're actually saving (not just reviewing)
        // When locked=true, we're in "Review" mode, when locked=false, we're in "Save" mode
        if (!this.locked && this.isKioskSurvey()) {
          this.callKioskApproveAPI()
        }
      }
    },

    scrollToPendingCard () {
      if (!this.pePendingCardScrollIdentifier || this.pePendingCardScrollIdentifier !== this.cardIdentifier) return

      this.peSetPendingCardScrollIdentifier(null)

      this.$nextTick().then(_ => {
        requestAnimationFrame(_ => {
          this.$refs.cardBody?.$el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      })
    },

    ...mapActions(PARTY_NAMESPACE, [
      'peDeletePartyDocument',
      'pePartySetOthersUpdated',
      'peSetPendingCardScrollIdentifier',
      'peSurveyApproveKioskChanges',
      'peSurveyCardAcceptCard',
      'peSurveyCardRejectCard',
      'peSurveyRejectKioskChanges',
    ]),
  },
}
</script>

<style scoped>
.section-card-body {
  scroll-margin: 150px;
}

.pe-card-survey {
  border: 5px dashed orange;
}
</style>
